import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CanvasAPI, CanvasYjsProvider } from "@beeos/canvas-client";
import { CanvasProvider, CanvasSurfaceList, useCanvas, useRelayMessageHandler } from "@beeos/a2ui";
import type { A2UIClientEventMessage, EditorSnapshot } from "@beeos/a2ui";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";

const CANVAS_API_URL = import.meta.env.VITE_CANVAS_API_URL ?? "https://canvas-api.beeos.ai";
const CANVAS_RELAY_URL = import.meta.env.VITE_CANVAS_RELAY_URL ?? "wss://canvas-relay.beeos.ai";

type ViewState =
  | { kind: "loading" }
  | { kind: "password"; slug: string }
  | { kind: "error"; message: string }
  | { kind: "revoked" }
  | { kind: "viewing"; slug: string; canvasId: string; connected: boolean; shareToken?: string; allowInteraction: boolean };

export function App() {
  const { t } = useTranslation();
  const slug = getSlugFromPath();
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  const [password, setPassword] = useState("");
  const providerRef = useRef<CanvasYjsProvider | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const api = useMemo(
    () =>
      new CanvasAPI({
        baseUrl: CANVAS_API_URL,
        getAuthHeaders: () => ({}),
      }),
    [],
  );

  useEffect(() => {
    return () => {
      providerRef.current?.disconnect();
      providerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!slug) {
      setState({ kind: "error", message: t("viewer.invalidShareLink") });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const share = await api.getShare(slug);
        if (cancelled) return;
        if (!share) {
          setState({ kind: "error", message: t("viewer.shareNotFound") });
          return;
        }
        if (share.mode === "password") {
          const cachedToken = getCachedShareToken(slug);
          if (cachedToken) {
            startViewing(slug, share.canvasId, share.allowInteraction, cachedToken);
            return;
          }
          setState({ kind: "password", slug });
          return;
        }
        startViewing(slug, share.canvasId, share.allowInteraction);
      } catch (err) {
        if (cancelled) return;
        setState({ kind: "error", message: (err as Error).message });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const startViewing = (slug: string, canvasId: string, allowInteraction: boolean, shareToken?: string) => {
    setState({ kind: "viewing", slug, canvasId, connected: false, shareToken, allowInteraction });
  };

  const handleSendAction = useCallback(
    (_surfaceId: string, action: A2UIClientEventMessage) => {
      const envelope = action as { userAction?: { name?: string; surfaceId?: string; sourceComponentId?: string; timestamp?: string; context?: Record<string, unknown> } };
      const ua = envelope.userAction;
      if (!ua) return;
      providerRef.current?.sendAction(ua.surfaceId ?? _surfaceId, {
        name: ua.name ?? "action",
        sourceComponentId: ua.sourceComponentId ?? "",
        timestamp: ua.timestamp ?? new Date().toISOString(),
        context: ua.context ?? {},
      });
    },
    [],
  );

  const handlePasswordSubmit = async () => {
    if (state.kind !== "password") return;
    try {
      const { share, shareToken } = await api.verifySharePassword(state.slug, password);
      cacheShareToken(state.slug, shareToken);
      startViewing(state.slug, share.canvasId, share.allowInteraction, shareToken);
    } catch {
      alert(t("viewer.invalidPassword"));
    }
  };

  if (state.kind === "loading") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="h-6 w-6 border-2 border-border border-t-primary rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground">{t("viewer.loadingCanvas")}</p>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-sm text-destructive">{state.message}</p>
      </div>
    );
  }

  if (state.kind === "revoked") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="flex flex-col items-center gap-3 p-8 rounded-xl border border-border max-w-sm w-full text-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
            <circle cx="12" cy="12" r="10" />
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
          </svg>
          <h2 className="text-lg font-semibold text-foreground">{t("viewer.shareRevoked")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("viewer.shareRevokedDescription")}
          </p>
        </div>
      </div>
    );
  }

  if (state.kind === "password") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="flex flex-col gap-4 p-8 rounded-xl border border-border max-w-sm w-full">
          <h2 className="text-lg font-semibold text-foreground">
            {t("viewer.passwordProtected")}
          </h2>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handlePasswordSubmit()}
            placeholder={t("viewer.enterPassword")}
            className="px-3.5 py-2.5 rounded-lg border border-input text-sm outline-none focus:ring-2 focus:ring-ring"
            autoFocus
          />
          <button
            onClick={handlePasswordSubmit}
            className="px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity cursor-pointer"
          >
            {t("viewer.viewCanvas")}
          </button>
        </div>
      </div>
    );
  }

  const canInteract = state.kind === "viewing" && state.allowInteraction;

  return (
    <CanvasProvider
      sessionId={slug}
      canvasAvailable
      onSendAction={canInteract ? handleSendAction : undefined}
    >
      <ViewerRelayBridge
        state={state}
        setState={setState}
        providerRef={providerRef}
        setActionError={setActionError}
      />
      <ViewerUI state={state} actionError={actionError} />
    </CanvasProvider>
  );
}

function ViewerRelayBridge({
  state,
  setState,
  providerRef,
  setActionError,
}: {
  state: ViewState;
  setState: React.Dispatch<React.SetStateAction<ViewState>>;
  providerRef: React.MutableRefObject<CanvasYjsProvider | null>;
  setActionError: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  const { t } = useTranslation();
  const handleRelayMsg = useRelayMessageHandler();

  useEffect(() => {
    if (state.kind !== "viewing") return;
    const { canvasId, shareToken, slug } = state;

    providerRef.current?.disconnect();

    const provider = new CanvasYjsProvider({
      relayUrl: CANVAS_RELAY_URL,
      shareSlug: slug,
      role: "viewer",
      token: shareToken,
      maxReconnectAttempts: 8,
    });
    providerRef.current = provider;

    provider.connect(canvasId, {
      onSync: (data) => handleRelayMsg(data),
      onSyncComplete: () => {
        setState((prev) =>
          prev.kind === "viewing" && !prev.connected ? { ...prev, connected: true } : prev,
        );
      },
      onAwareness: (data) => handleRelayMsg(data),
      onEditors: (data) => handleRelayMsg(data),
      onPresence: (data) => handleRelayMsg(data),
      onConnection: (connected) => {
        setState((prev) =>
          prev.kind === "viewing" ? { ...prev, connected } : prev,
        );
      },
      onActionResult: () => setActionError(null),
      onActionError: (msg) => {
        if (msg.error === "share_revoked") {
          setState({ kind: "revoked" });
          return;
        }
        const errorMessages: Record<string, string> = {
          agent_offline: t("viewer.agentOffline"),
          rate_limited: t("viewer.rateLimited"),
          timeout: t("viewer.actionTimeout"),
          processing_failed: t("viewer.actionProcessingFailed"),
        };
        setActionError(errorMessages[msg.error] ?? t("viewer.actionFailed"));
        setTimeout(() => setActionError(null), 5000);
      },
      onShareRevoked: () => setState({ kind: "revoked" }),
      onError: () => setState({
        kind: "error",
        message: t("viewer.tooManyViewers"),
      }),
    });

    return () => {
      provider.disconnect();
      if (providerRef.current === provider) providerRef.current = null;
    };
  }, [state.kind === "viewing" ? `${(state as { canvasId: string }).canvasId}` : ""]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function ViewerUI({ state, actionError }: { state: ViewState; actionError: string | null }) {
  const { t } = useTranslation();
  const { editors } = useCanvas();

  if (state.kind !== "viewing") return null;

  return (
    <div className="flex flex-col min-h-screen bg-muted/30">
      <header className="sticky top-0 z-50 flex items-center justify-between px-6 py-3 border-b border-border bg-background/80 backdrop-blur-sm shrink-0">
        <span className="text-sm font-semibold text-foreground">{t("viewer.header")}</span>
        <div className="flex items-center gap-3">
          <EditorAvatars editors={editors} />
          <span
            className={[
              "px-2 py-0.5 rounded-full text-[11px] font-medium text-white",
              state.connected ? "bg-green-500" : "bg-yellow-500",
            ].join(" ")}
          >
            {state.connected ? t("viewer.live") : t("viewer.connecting")}
          </span>
        </div>
      </header>
      {actionError && (
        <div className="px-6 py-2 bg-destructive/10 text-destructive text-sm text-center animate-in fade-in slide-in-from-top-1">
          {actionError}
        </div>
      )}
      <main className="flex-1 min-h-0 overflow-auto">
        <div className="max-w-4xl mx-auto py-6">
          <CanvasSurfaceList readOnly />
        </div>
      </main>
    </div>
  );
}

function EditorAvatars({ editors }: { editors: EditorSnapshot[] }) {
  const { t } = useTranslation();
  if (editors.length === 0) return null;

  return (
    <div className="flex items-center [&>*+*]:-ml-1.5" aria-label={t("viewer.canvasEditors")}>
      {editors.slice(0, 8).map((u, idx) => {
        const isEditing = u.online && u.type === "agent";
        const isOnline = u.online && u.type !== "agent";
        const displayName = u.displayName || u.name || u.id;
        const baseZ = Math.min(editors.length, 8) - idx;
        return (
          <HoverCardPrimitive.Root key={u.id} openDelay={300} closeDelay={200}>
            <HoverCardPrimitive.Trigger asChild>
              <div
                className="relative flex h-7 w-7 shrink-0 items-center justify-center cursor-default hover:z-20"
                style={{ zIndex: baseZ }}
              >
                <div
                  className={[
                    "flex h-6 w-6 items-center justify-center rounded-full ring-2 ring-background overflow-hidden",
                    isEditing ? "animate-[presence-pulse_2s_ease-in-out_infinite]" : "",
                    !u.online ? "opacity-40 grayscale" : "",
                  ].join(" ")}
                  style={{ backgroundColor: u.avatarUrl ? undefined : colorForUser(u.id) }}
                >
                  {u.avatarUrl ? (
                    <img src={u.avatarUrl} alt={displayName} className="h-full w-full rounded-full object-cover" />
                  ) : (
                    <span className="text-[10px] font-medium text-white">{userInitials(u.displayName || u.name, u.id)}</span>
                  )}
                </div>
                {isOnline && !isEditing && (
                  <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-green-500 ring-1 ring-background" />
                )}
              </div>
            </HoverCardPrimitive.Trigger>
            <HoverCardPrimitive.Portal>
              <HoverCardPrimitive.Content
                side="bottom"
                sideOffset={4}
                className="z-50 w-56 rounded-lg border border-border/40 bg-popover p-3 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full overflow-hidden"
                    style={{ backgroundColor: u.avatarUrl ? undefined : colorForUser(u.id) }}
                  >
                    {u.avatarUrl ? (
                      <img src={u.avatarUrl} alt={displayName} className="h-full w-full rounded-full object-cover" />
                    ) : (
                      <span className="text-[10px] font-medium text-white">{userInitials(u.displayName || u.name, u.id)}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <p className="text-sm font-semibold truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground">
                      {u.type === "agent" ? t("viewer.agent") : t("viewer.user")} · {u.role === "owner" ? t("viewer.owner") : u.role === "editor" ? t("viewer.editor") : u.role}
                      {u.online ? "" : ` · ${t("viewer.offline")}`}
                    </p>
                    {u.online && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-green-600 dark:text-green-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        {t("viewer.online")}
                      </span>
                    )}
                  </div>
                </div>
                {(u.agentId || u.id) && (
                  <p className="mt-2 text-[10px] text-muted-foreground/70 font-mono truncate select-all" title={u.agentId || u.id}>
                    ID: {u.agentId || u.id}
                  </p>
                )}
              </HoverCardPrimitive.Content>
            </HoverCardPrimitive.Portal>
          </HoverCardPrimitive.Root>
        );
      })}
      {editors.length > 8 && (
        <div className="flex h-7 w-7 items-center justify-center">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground ring-2 ring-background">
            +{editors.length - 8}
          </div>
        </div>
      )}
    </div>
  );
}

const SHARE_TOKEN_KEY_PREFIX = "canvas-share-token:";
const TOKEN_EXPIRY_BUFFER_S = 60;

function cacheShareToken(slug: string, token: string): void {
  try {
    localStorage.setItem(`${SHARE_TOKEN_KEY_PREFIX}${slug}`, token);
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
  }
}

function getCachedShareToken(slug: string): string | null {
  try {
    const token = localStorage.getItem(`${SHARE_TOKEN_KEY_PREFIX}${slug}`);
    if (!token) return null;

    const payload = JSON.parse(atob(token.split(".")[1]));
    if (typeof payload.exp === "number" && payload.exp - TOKEN_EXPIRY_BUFFER_S > Date.now() / 1000) {
      return token;
    }

    localStorage.removeItem(`${SHARE_TOKEN_KEY_PREFIX}${slug}`);
  } catch {
    // Malformed token or localStorage error — treat as missing
  }
  return null;
}

const PRESENCE_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e",
  "#06b6d4", "#3b82f6", "#8b5cf6", "#ec4899",
];

function colorForUser(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  }
  return PRESENCE_COLORS[Math.abs(hash) % PRESENCE_COLORS.length]!;
}

function userInitials(name?: string, userId?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  return (userId ?? "??").slice(0, 2).toUpperCase();
}

function getSlugFromPath(): string | null {
  const parts = window.location.pathname.split("/");
  const shareIdx = parts.indexOf("share");
  if (shareIdx >= 0 && parts[shareIdx + 1]) {
    return parts[shareIdx + 1];
  }
  return parts[parts.length - 1] || null;
}
